import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import BundleCreateRow, { BLANK_BUNDLE } from "./BundleCreateRow";
import BundleRow from "./BundleRow";

/** BundlesManager — CRUD for the 1 / 3 / 5 pack bundles (or any custom pack). */
const BundlesManager = ({ authClient }) => {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(BLANK_BUNDLE);
  const [savingId, setSavingId] = useState(null);

  const reload = async () => {
    try {
      const { data } = await authClient().get("/admin/cms/bundles");
      setItems(data);
    } catch {
      toast.error("Couldn't load bundles");
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFieldChange = (idx, key, val) =>
    setItems((prev) =>
      prev.map((b, i) => (i === idx ? { ...b, [key]: val, _dirty: true } : b))
    );

  const onSave = async (b) => {
    setSavingId(b.id);
    try {
      await authClient().put(`/admin/cms/bundles/${b.id}`, {
        slug: b.slug,
        name: b.name,
        pairs: Number(b.pairs),
        price: Number(b.price),
        saving: Number(b.saving),
        description: b.description || "",
        tag: b.tag || "",
        featured: Boolean(b.featured),
        is_active: Boolean(b.is_active),
        sort_order: Number(b.sort_order) || 0,
      });
      toast.success(`${b.name} saved`);
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const onDelete = async (b) => {
    if (!window.confirm(`Delete bundle "${b.name}"?`)) return;
    try {
      await authClient().delete(`/admin/cms/bundles/${b.id}`);
      toast.success("Bundle removed");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  const onCreate = async () => {
    if (!draft.slug || !draft.name) {
      toast.error("Slug and name are required");
      return;
    }
    try {
      await authClient().post("/admin/cms/bundles", {
        ...draft,
        pairs: Number(draft.pairs),
        price: Number(draft.price),
        saving: Number(draft.saving),
        sort_order: Number(draft.sort_order) || 0,
      });
      toast.success(`${draft.name} created`);
      setCreating(false);
      setDraft(BLANK_BUNDLE);
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Create failed");
    }
  };

  return (
    <div data-testid="bundles-manager">
      <div className="flex items-center justify-between mb-4">
        <p className="overline">Bundles ({items.length})</p>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="btn-secondary !px-4 !py-2 text-xs"
          data-testid="bundle-add-button"
        >
          <Plus size={14} className="mr-1.5" /> {creating ? "Cancel" : "New bundle"}
        </button>
      </div>

      {creating && (
        <BundleCreateRow draft={draft} setDraft={setDraft} onCreate={onCreate} />
      )}

      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-xs min-w-[900px]">
          <thead className="border-b border-line">
            <tr className="text-left">
              <th className="px-3 py-3 overline">Name / Slug</th>
              <th className="px-3 py-3 overline">Pairs</th>
              <th className="px-3 py-3 overline">Price</th>
              <th className="px-3 py-3 overline">Save</th>
              <th className="px-3 py-3 overline">Tag</th>
              <th className="px-3 py-3 overline">Featured</th>
              <th className="px-3 py-3 overline">Active</th>
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((b, idx) => (
              <BundleRow
                key={b.id}
                b={b}
                idx={idx}
                saving={savingId === b.id}
                onFieldChange={onFieldChange}
                onSave={onSave}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BundlesManager;
